<!--
  The Solo chat panel (C0b P5, C0B-DESIGN 3.1): header, side column (bots with Solo pinned, earlier ChatKit chats, one
  search), and the bot's thread: earlier sessions stitched above the head conversation, or an old chat read only.
-->
<div class="solo-chat" data-testid="solo-chat" [attr.data-mode]="mode" [class.side-open]="sideOpen">
  <header class="head">
    <button type="button" class="ib side-toggle" data-testid="solo-chat-side-toggle" [attr.aria-expanded]="sideOpen"
      [attr.aria-label]="sideOpen ? t('agents.solo_chat.hide_chats') : t('agents.solo_chat.show_chats')"
      [title]="sideOpen ? t('agents.solo_chat.hide_chats') : t('agents.solo_chat.show_chats')" (click)="toggleSide()">
      <ion-icon name="chatbubbles-outline" aria-hidden="true"></ion-icon>
    </button>
    <ion-icon name="sparkles-outline" class="spark" aria-hidden="true"></ion-icon>
    <h2 class="title" data-testid="solo-chat-title">{{ title }}</h2>
    <div class="head-actions">
      <button type="button" class="ib" data-testid="solo-chat-copy-link" [disabled]="!chatLink"
        [attr.aria-label]="linkCopied ? t('agents.solo_chat.link_copied') : t('agents.solo_chat.copy_link')"
        [title]="linkCopied ? t('agents.solo_chat.link_copied') : t('agents.solo_chat.copy_link')" (click)="copyLink()">
        <ion-icon [name]="linkCopied ? 'checkmark-outline' : 'link-outline'" aria-hidden="true"></ion-icon>
      </button>
      @if (linkCopied) { <span class="sr-only" role="status">{{ t('agents.solo_chat.link_copied') }}</span> }
      <span class="sr-only" role="status" data-testid="solo-chat-live-status">{{ liveStatus }}</span>
      @if (mode !== 'full') {
        <button type="button" class="ib desktop-only" data-testid="solo-chat-expand" [attr.aria-label]="t('agents.solo_chat.expand')"
          [title]="t('agents.solo_chat.expand')" (click)="expandMode()"><ion-icon name="expand-outline" aria-hidden="true"></ion-icon></button>
      }
      @if (mode !== 'panel') {
        <button type="button" class="ib desktop-only" data-testid="solo-chat-collapse" [attr.aria-label]="t('agents.solo_chat.collapse')"
          [title]="t('agents.solo_chat.collapse')" (click)="collapseMode()"><ion-icon name="contract-outline" aria-hidden="true"></ion-icon></button>
      }
      <button type="button" class="ib" data-testid="solo-chat-close" [attr.aria-label]="t('agents.solo_chat.close')"
        [title]="t('agents.solo_chat.close')" (click)="close()"><ion-icon name="close-outline" aria-hidden="true"></ion-icon></button>
    </div>
  </header>

  <div class="body">
    @if (sideOpen) { <div class="side-backdrop" aria-hidden="true" (click)="toggleSide()"></div> }
    <aside class="side" data-testid="solo-chat-side" [attr.aria-label]="t('agents.solo_chat.chats')">
      <label class="search">
        <ion-icon name="search-outline" aria-hidden="true"></ion-icon>
        <input type="search" data-testid="solo-chat-search" [value]="query" [placeholder]="t('agents.solo_chat.search')"
          [attr.aria-label]="t('agents.solo_chat.search')" (input)="onSearch($any($event.target).value)" />
      </label>

      <h3 class="side-title">{{ t('agents.solo_chat.bots') }}</h3>
      <ul class="rows" data-testid="solo-chat-bots">
        @for (bot of shownBots; track bot.agentId) {
          <li>
            <button type="button" class="row" data-testid="solo-chat-bot" [attr.data-solo]="bot.isSolo ? 'true' : null"
              [class.active]="isSelectedBot(bot.agentId)" [attr.aria-current]="isSelectedBot(bot.agentId) ? 'true' : null" (click)="selectBot(bot)">
              <span class="avatar">{{ bot.name.charAt(0) }}</span><span class="row-name">{{ bot.name }}</span>
            </button>
          </li>
        }
      </ul>
      @if (!botsLoading && !shownBots.length) { <p class="side-empty" data-testid="solo-chat-bots-empty">{{ t('agents.solo_chat.no_matches') }}</p> }

      <h3 class="side-title">{{ t('agents.solo_chat.earlier_chats') }}</h3>
      <ul class="rows" data-testid="solo-chat-legacy-list">
        @for (row of legacyRows; track row.threadId) {
          <li>
            <button type="button" class="row legacy" data-testid="solo-chat-legacy-row" [class.active]="isSelectedLegacy(row.threadId)"
              [attr.aria-current]="isSelectedLegacy(row.threadId) ? 'true' : null" (click)="selectLegacy(row.threadId)">
              <ion-icon name="archive-outline" aria-hidden="true"></ion-icon>
              <span class="row-name">{{ row.title }}</span><small>{{ row.when }}</small>
            </button>
          </li>
        }
      </ul>
      @if (!legacyLoading && !legacyRows.length) {
        <p class="side-empty" data-testid="solo-chat-legacy-empty">{{ query.trim() ? t('agents.solo_chat.no_matches') : t('agents.solo_chat.no_earlier') }}</p>
      }
    </aside>

    <section class="main" role="region" [attr.aria-label]="title">
      @if (notice) {
        <div class="notice" role="alert" data-testid="solo-chat-notice"><ion-icon name="alert-circle-outline" aria-hidden="true"></ion-icon><span>{{ notice }}</span></div>
      }
      @if (agentStateNotice) {
        <div class="notice warn" role="status" data-testid="solo-chat-agent-state"><ion-icon name="alert-circle-outline" aria-hidden="true"></ion-icon><span>{{ agentStateNotice }}</span></div>
      }

      @if (selection?.kind === 'legacy') {
        <app-legacy-thread-view class="fill" [threadId]="$any(selection).threadId" (sendToSolo)="sendToSolo($event)"></app-legacy-thread-view>
      } @else if (threadLoading && !thread) {
        <div class="state" role="status" data-testid="solo-chat-loading"><ion-spinner name="crescent"></ion-spinner><span>{{ t('agents.solo_chat.loading') }}</span></div>
      } @else if (threadError) {
        <div class="notice" role="alert" data-testid="solo-chat-error">
          <ion-icon name="alert-circle-outline" aria-hidden="true"></ion-icon><span>{{ threadError }}</span>
          <button type="button" class="link-btn" (click)="retry()">{{ t('agents.solo_chat.retry') }}</button>
        </div>
      } @else if (thread) {
        <app-agents-conversation class="fill" layout="panel" data-testid="solo-chat-conversation"
          [agentId]="agentId" [agentName]="agentName" [chain]="chain" [headSessionId]="headSessionId" [startKey]="threadStartKey"
          (headChanged)="onHeadChanged($event)" (sessionOpened)="onSessionOpened($event)" (recordOpened)="onRecordOpened($event)"
          (scroll)="onConversationScroll()">
          <div conversationBefore class="before">
            @if (hasOlder) {
              <button type="button" class="load-older" data-testid="solo-chat-load-older" [disabled]="loadingOlder" (click)="loadOlderKeepingPlace()">
                {{ loadingOlder ? t('agents.solo_chat.earlier_loading') : t('agents.solo_chat.load_earlier') }}
              </button>
            }
            @for (session of earlier; track session.sessionId) {
              <div class="earlier" data-testid="solo-chat-earlier" [attr.data-session]="session.sessionId">
                @if (session.divider) {
                  <div class="divider" role="separator" data-testid="solo-chat-divider"><span>{{ session.divider }}</span></div>
                }
                @if (session.loading) {
                  <p class="earlier-state" role="status">{{ t('agents.solo_chat.earlier_loading') }}</p>
                } @else if (session.failed && !session.entries.length) {
                  <p class="earlier-state">{{ t('agents.solo_chat.earlier_failed') }}</p>
                }
                @if (session.partial) {
                  <p class="earlier-state" data-testid="solo-chat-earlier-partial">{{ t('agents.solo_chat.earlier_partial') }}</p>
                }
                @for (entry of session.entries; track entry.key) {
                  @if (entry.kind === 'user') {
                    <div class="mine-wrap" data-testid="solo-chat-earlier-user">
                      @if (entry.text) { <div class="mine">{{ entry.text }}</div> }
                      @if (entry.attachments?.length) { <app-agent-attachment-chips [attachments]="entry.attachments!"></app-agent-attachment-chips> }
                      @if (entry.text) {
                        <app-agent-message-actions [entry]="entry" [sessionId]="session.sessionId" (announce)="say($event)"></app-agent-message-actions>
                      }
                    </div>
                  } @else if (entry.kind === 'agent') {
                    <div class="msg" data-testid="solo-chat-earlier-agent">
                      @if (entry.text) {
                        <div class="bubble"><app-agents-markdown [text]="entry.text" [labels]="markdownLabels" (copied)="onCodeCopied($event)"></app-agents-markdown></div>
                      }
                      @if (entry.attachments?.length) { <app-agent-attachment-chips [attachments]="entry.attachments!"></app-agent-attachment-chips> }
                      <app-agent-message-actions [entry]="entry" [sessionId]="session.sessionId" [canRate]="true"
                        [feedback]="feedbackMap[session.sessionId + ':' + entry.seq] ?? null" [refusal]="feedbackRefusals[session.sessionId] ?? null"
                        (announce)="say($event)"></app-agent-message-actions>
                    </div>
                  } @else if (entry.kind === 'steps') {
                    <div class="steps" data-testid="solo-chat-earlier-steps">
                      <button type="button" class="steps-toggle" [attr.aria-expanded]="!!stepsOpen[session.sessionId + ':' + entry.key]"
                        [attr.aria-controls]="'steps-' + session.sessionId + '-' + entry.key" (click)="toggleSteps(session.sessionId + ':' + entry.key)">
                        <ion-icon [name]="stepsOpen[session.sessionId + ':' + entry.key] ? 'chevron-up-outline' : 'chevron-down-outline'" aria-hidden="true"></ion-icon>
                        {{ stepsOpen[session.sessionId + ':' + entry.key] ? t('agents.chat.steps.hide') : stepsLabel(entry) }}
                      </button>
                      @if (stepsOpen[session.sessionId + ':' + entry.key]) {
                        <ul class="steps-list" [id]="'steps-' + session.sessionId + '-' + entry.key">
                          @for (item of entry.items; track $index) {
                            <li [class.failed]="item.failed">{{ stepText(item.text) }}@if (item.text.params?.['tool'] && item.text.key !== 'agents.chat.activity.tool_other') { <small>{{ item.text.params!['tool'] }}</small> }</li>
                          }
                        </ul>
                      }
                    </div>
                  } @else if (entry.kind === 'activity') {
                    <div class="activity" [class.error]="entry.tone === 'error'">{{ text(entry.text) }}</div>
                  }
                }
              </div>
            }
            @if (headDivider && earlier.length) {
              <div class="divider" role="separator" data-testid="solo-chat-divider"><span>{{ headDivider }}</span></div>
            }
            @if (showStart) {
              <div class="start" data-testid="solo-chat-start">
                <p class="greeting" data-testid="solo-chat-greeting">{{ greeting }}</p>
                <div class="starters" role="group" [attr.aria-label]="t('agents.solo_chat.starters_label')">
                  @for (starter of starters; track starter.key) {
                    <button type="button" class="starter" data-testid="solo-chat-starter" (click)="sendStarter(starter.key)">{{ starter.text }}</button>
                  }
                </div>
              </div>
            }
          </div>
        </app-agents-conversation>
      }
    </section>
  </div>
</div>
